import * as Sentry from "@sentry/react";
import type { Observability, ObservabilityUser } from "./index";
import { env, isProd, isStaging } from "@/lib/env";

let initialized = false;

/** Initialize Sentry once at app boot, only when a DSN is configured.
 *  No-op in environments without VITE_SENTRY_DSN — keeps local dev quiet. */
export function initSentry() {
  if (initialized) return;
  if (!env.VITE_SENTRY_DSN) return;

  Sentry.init({
    dsn: env.VITE_SENTRY_DSN,
    environment: env.VITE_APP_ENV,
    release: env.VITE_SENTRY_RELEASE,
    // Capture 10% of transactions in prod, 100% in staging for visibility.
    tracesSampleRate: isProd ? 0.1 : isStaging ? 1.0 : 0,
    // Session replay is opt-in — not enabled by default.
    integrations: [Sentry.browserTracingIntegration()],
    sendDefaultPii: false,
  });

  initialized = true;
}

export const sentryObservability: Observability = {
  captureError(error, context) {
    if (!initialized) {
      console.error("[obs] error (sentry not initialized)", error, context ?? {});
      return;
    }
    Sentry.captureException(error, context ? { extra: context } : undefined);
  },
  captureEvent(name, props) {
    if (!initialized) return;
    Sentry.captureMessage(name, { level: "info", extra: props });
  },
  setUser(user: ObservabilityUser | null) {
    if (!initialized) return;
    Sentry.setUser(
      user ? { id: user.id, email: user.email, segment: user.businessId?.toString() } : null,
    );
  },
};
